<template>
    <div
        id="layout-wrap"
        :class="[showSidebar ? 'zs-shell' : 'zs-shell--bare', { 'is-mobilenav-open': mobileNav.isOpen.value }]"
        :data-app="activeApp"
    >
        <Sidebar v-if="showSidebar" class="zs-shell__sidebar" />
        <div v-if="showSidebar" class="zs-shell__scrim" @click="mobileNav.close()" />

        <main :class="{ 'zs-shell__workspace': showSidebar }">
            <slot />
            <div class="version wide" :class="{ nonprod: !isProduction }">{{ displayVersion }}</div>
        </main>

        <SysBanner v-if="showSidebar && incidentMessage" class="zs-shell__banner" variant="incident" @message-click="openOrAckPlatformStatus">
            {{ incidentMessage }}
            <template #actions>
                <a href="https://status.zyno.app" target="zynoStatus" style="color: inherit; text-decoration: underline">Status page</a>
            </template>
        </SysBanner>
    </div>
</template>

<script lang="ts" setup>
import { compact } from 'lodash';
import { computed, watch } from 'vue';
import { useRoute } from 'vue-router';

import { usePlatformIncidents } from '@/modules/core/exports/incidents';
import Sidebar from '@/shared/components/v2/sidebar.vue';
import SysBanner from '@/shared/components/v2/sys-banner.vue';
import { useMobileNav } from '@/shared/helpers/mobile-nav';

const isProduction = import.meta.env.VITE_APP_ENV === 'production';
const zwVersion = navigator.userAgent.match(/^Zyno(Desktop|Workstation)\/([\d.]+)/)?.[2]?.replace(/\.0$/, '');
const displayVersion = compact([!isProduction && import.meta.env.VITE_APP_ENV, zwVersion, import.meta.env.VITE_APP_VERSION]).join(' | ');

const props = defineProps<{
    showSidebar?: boolean;
}>();

const route = useRoute();
// Surface the active module to CSS — e.g. Talk wants a 180px sidebar instead of 220px.
const activeApp = computed(() => route.path.match(/^\/([a-z0-9-]+)/)?.[1] ?? null);

// Mobile drawer: collapse it whenever the route changes so navigating closes it
// — except when a parent-with-children tap asked to stay open (see useMobileNav).
const mobileNav = useMobileNav();
watch(
    () => route.fullPath,
    () => mobileNav.autoClose()
);

const { openPlatformIncidents, openOrAckPlatformStatus } = usePlatformIncidents();
const incidentMessage = computed(() => {
    const incident = openPlatformIncidents.value?.[0];
    if (!incident) return null;
    return incident.name || 'The ZynoSuite platform is experiencing issues.';
});
</script>

<style scoped>
@reference '#tw.css';

#layout-wrap {
    @apply min-w-0;
}

#layout-wrap.zs-shell--bare {
    @apply flex-1 flex flex-col p-4 gap-3;
}

main {
    @apply flex flex-col overflow-hidden;
}

.zs-shell__workspace {
    /* No padding/gap here — the workspace IS the warm canvas. Module headers
       (canvas-topbar, talk-header) sit flush at the top; content panes manage
       their own internal padding. */
    @apply flex-1 flex flex-col overflow-hidden min-w-0;
    background: var(--bg);
}

.zs-shell--bare main {
    @apply flex-1;
}

.version {
    @apply absolute bottom-0 left-0 w-full text-center text-xs text-dark-4 p-4 -z-10;

    &.nonprod {
        @apply text-err-fill;
    }
}

/* The sidebar shows env + version when present; hide the bottom floater there
   to avoid duplication. Login / bare layouts keep the floater. */
#layout-wrap.zs-shell .version {
    display: none;
}

:deep(main) {
    /* main will contain a single div: the active route's component */
    /* that will _typically_ contain a "header" and "body" div */
    > div {
        @apply flex-1 flex flex-col overflow-hidden /* min-h-0 */;

        &:not(.wide) {
            @apply mx-auto w-full max-w-screen-xl;
        }

        &.wide.auto {
            @apply self-center min-w-screen-xl;
        }

        > .header {
            @apply py-2 /* can't use gap-2 on the parent or the shadow on the selects gets cut off */;
        }

        > .body {
            @apply flex-1 bg-surface rounded-md relative;
        }
    }
}

@media (max-width: 768px) {
    /* Salespoint only: transparent on mobile so the slate body shows through
       (incl. behind the safe-area insets) rather than the warm canvas. */
    .zs-shell__workspace:has(#sales-salespoint) {
        background: transparent;
    }

    .version {
        @apply p-3;
    }

    :deep(main) {
        > div {
            &.wide.auto {
                @apply min-w-full max-w-full;
            }
        }
    }
}
</style>
